import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const THEME_STORAGE_KEY = "shotboard.theme";

const themes = ["system", "light", "dark"] as const;

export type Theme = (typeof themes)[number];

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: string) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Narrows untrusted persisted or form data to a supported theme. */
export function parseTheme(value: string | null): Theme {
  return themes.find((theme) => theme === value) ?? "system";
}

/** Resolves a theme selection against the current operating-system preference. */
export function resolveTheme(theme: Theme, prefersDark: boolean): Exclude<Theme, "system"> {
  if (theme === "system") {
    return prefersDark ? "dark" : "light";
  }

  return theme;
}

/** Owns local theme preference and keeps the document color scheme synchronized. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() =>
    parseTheme(window.localStorage.getItem(THEME_STORAGE_KEY)),
  );

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");

    /** Applies the effective theme without duplicating theme state. */
    const applyTheme = () => {
      document.documentElement.dataset.theme = resolveTheme(theme, media.matches);
    };

    applyTheme();
    media.addEventListener("change", applyTheme);
    return () => media.removeEventListener("change", applyTheme);
  }, [theme]);

  /** Persists only validated theme values. */
  const setTheme = useCallback((nextTheme: string) => {
    const parsedTheme = parseTheme(nextTheme);
    window.localStorage.setItem(THEME_STORAGE_KEY, parsedTheme);
    setThemeState(parsedTheme);
  }, []);

  const value = useMemo(() => ({ theme, setTheme }), [setTheme, theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Reads the theme context from a validated provider boundary. */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);

  if (context === null) {
    throw new Error("useTheme must be used inside ThemeProvider.");
  }

  return context;
}
